import { UserRouteV1Model } from '@core/models/entities/implementations/user-route-v1';
import { RawKpi } from '@core/models/kpi.model';
import { PlanEditGesture } from '@core/models/plan-edit-gesture.model';
import {
  PlanEditHolder,
  PlanWatchHolder,
  WatchOverlay,
  WatchPatchEvent
} from '@core/models/plan-edit.model';
import { PlanFinding } from '@core/models/plan-finding.model';

import { EMPTY_WATCH_OVERLAY } from './plan-watch-overlay';
import { WatchImpactState, initialWatchImpactState } from './watch-impact';

export type PlanWatchPhase =
  | 'idle'
  | 'subscribing'
  | 'live'
  | 'held-unavailable'
  | 'unavailable';

export interface PlanWatchState {
  /** The plan this tab watches, or null when it watches none. */
  planId: string | null;
  /**
   * `subscribing` until GBE first answers for `planId`; `idle` once it says nobody holds it.
   * `unavailable` when GBE refused the watch, which says nothing about who holds the plan.
   */
  phase: PlanWatchPhase;
  /** A watch is in flight: patches wait for its snapshot rather than apply to a stale base. */
  awaitingSnapshot: boolean;
  holder: PlanWatchHolder | null;
  /** The flavours the session projects: views of any other show the stored plan. */
  projections: string[];
  revision: number;
  recomputePending: boolean;
  /**
   * The owed recompute failed: timings stay provisional. Kept apart from `recomputePending`, which
   * orders states: a save or a resume retries the recompute at the same revision.
   */
  recomputeFailed: boolean;
  overlay: WatchOverlay;
  findings: PlanFinding[] | null;
  /** The session's plan KPIs; null until a state carries them. */
  planKpis: RawKpi[] | null;
  pendingPatches: WatchPatchEvent[];
}

export const initialPlanWatchState: PlanWatchState = {
  planId: null,
  phase: 'idle',
  awaitingSnapshot: false,
  holder: null,
  projections: [],
  revision: 0,
  recomputePending: false,
  recomputeFailed: false,
  overlay: EMPTY_WATCH_OVERLAY,
  findings: null,
  planKpis: null,
  pendingPatches: []
};

const NO_FLAVOURS: readonly string[] = [];

/** The flavours whose views show the session live. The same array while it holds, for signals. */
export function liveFlavours(watch: PlanWatchState): readonly string[] {
  return watch.phase === 'live' ? watch.projections : NO_FLAVOURS;
}

export function watchShowsLive(watch: PlanWatchState, flavour: string): boolean {
  return liveFlavours(watch).includes(flavour);
}

/** Whether `planId`'s page shows the watched session live rather than the stored plan. */
export function watchIsLiveFor(watch: PlanWatchState, planId: string | null): boolean {
  return planId !== null && watch.planId === planId && watch.phase === 'live';
}

export type PlanKpisMode = 'stored' | 'saved-plan' | 'live' | 'provisional';

const NO_KPIS: RawKpi[] = [];

/**
 * The plan KPIs a plan page shows. While its plan is watched live the stored ones describe the plan
 * before the session's edits: they give way to the session's own, or are marked as the saved plan's
 * when GBE sends none.
 */
export function planKpisView(
  watch: PlanWatchState,
  planId: string | null,
  stored: RawKpi[] | undefined
): { kpis: RawKpi[]; mode: PlanKpisMode } {
  if (!watchIsLiveFor(watch, planId)) return { kpis: stored ?? NO_KPIS, mode: 'stored' };
  if (watch.planKpis === null) return { kpis: stored ?? NO_KPIS, mode: 'saved-plan' };
  const settled = !watch.recomputePending && !watch.recomputeFailed;
  return { kpis: watch.planKpis, mode: settled ? 'live' : 'provisional' };
}

export type PlanEditMode =
  | 'off'
  | 'opening'
  | 'editing'
  | 'saving'
  | 'closing'
  | 'disconnected';
export type PlanEditTimings = 'settled' | 'recomputing' | 'provisional';

export interface SessionGesture {
  clientSeq: number;
  gesture: PlanEditGesture;
  touchedRouteUids: string[];
}

export interface InFlightGesture extends SessionGesture {
  /** The touched routes as the gesture is predicted to leave them, by route uid. */
  predicted: Record<string, UserRouteV1Model>;
}

export interface PinnedStop {
  routeUid: string;
  at: string;
}

export interface ImpactSnapshot {
  findings: PlanFinding[];
  planKpis: RawKpi[] | null;
}

export interface ImpactRound {
  before: ImpactSnapshot;
  after: ImpactSnapshot;
  touchedRouteUids: string[];
  /** `before.findings` are unknown: the change started from no state that carried any. */
  storedBefore?: true;
}

export interface PlanEditImpactState {
  /** DL's last settled findings and KPIs in this session; null until it has settled once. */
  settledAfter: ImpactSnapshot | null;
  /** The settled state the round in progress is measured from; null between rounds. */
  baseline: ImpactSnapshot | null;
  /**
   * A round is in progress with no settled state to measure it from: it ends recording no round,
   * so the panel keeps the last one it showed.
   */
  roundUnmeasured: boolean;
  /**
   * DL accepted a gesture of the round in progress: a refusal can no longer drop the round, and a
   * recompute answers for the round rather than for the state it started from.
   */
  baselineApplied: boolean;
  touchedRouteUids: string[];
  lastRound: ImpactRound | null;
}

export const initialPlanEditImpactState: PlanEditImpactState = {
  settledAfter: null,
  baseline: null,
  roundUnmeasured: false,
  baselineApplied: false,
  touchedRouteUids: [],
  lastRound: null
};

/** This tab's own edit session, at most one at a time. */
export interface PlanEditSessionState {
  mode: PlanEditMode;
  planId: string | null;
  sessionId: string | null;
  resumable: boolean;
  nextClientSeq: number;
  inFlight: InFlightGesture[];
  /** The undo stack, oldest first; entries from `cursor` on are the redo tail. */
  applied: SessionGesture[];
  cursor: number;
  /** One undo or redo steps a whole gesture, so one is outstanding at a time. */
  awaitingUndo: {
    clientSeq: number;
    touchedRouteUids: string[];
    direction: 'undo' | 'redo';
  } | null;
  /**
   * Pins under the undo stack: those held at the last save, plus those of gestures the stack has
   * dropped. DL sends no pins back, so the shown set replays `applied` over this one.
   */
  pinsAtSave: Record<string, PinnedStop>;
  /** The route starts under the undo stack, by route uid, kept the same way as `pinsAtSave`. */
  routeStartsAtSave: Record<string, string>;
  /** Gestures since open or the last save. Not `cursor`: the stack is capped, the count is not. */
  unsavedCount: number;
  recomputingRouteUids: string[];
  timings: PlanEditTimings;
  /** The last refusal, with the gesture it refused when DL named one, so it can be retried. */
  lastError: {
    code: string;
    details: Record<string, unknown>;
    /** DL's or GBE's own explanation; absent when it sent none or the client raised the error. */
    message?: string;
    /** The refusal ended a recompute: the edit was applied, only its timings failed. */
    recompute?: true;
    gesture?: PlanEditGesture;
  } | null;
  /** Routes the session created, by the uid DL minted, which no stored page of the plan holds. */
  openedRouteUids: string[];
  removedRouteUids: string[];
  /**
   * The opened routes the last save stored. Once the session ends, the others are gone with its
   * unsaved changes.
   */
  routesOpenedAtSave: string[];
  /**
   * Plans whose session was ended to hand them to the assistant. They outlive the session, so edit
   * mode reopens when the assistant releases one.
   */
  handedOverPlanIds: string[];
  impact: PlanEditImpactState;
}

export const initialPlanEditSessionState: PlanEditSessionState = {
  mode: 'off',
  planId: null,
  sessionId: null,
  resumable: false,
  nextClientSeq: 1,
  inFlight: [],
  applied: [],
  cursor: 0,
  awaitingUndo: null,
  pinsAtSave: {},
  routeStartsAtSave: {},
  unsavedCount: 0,
  recomputingRouteUids: [],
  timings: 'settled',
  lastError: null,
  openedRouteUids: [],
  removedRouteUids: [],
  routesOpenedAtSave: [],
  handedOverPlanIds: [],
  impact: initialPlanEditImpactState
};

export interface PlanEditState {
  /** Who holds each plan in a live edit session, by plan id. */
  holders: Record<string, PlanEditHolder>;
  /** The plan_data_revision each plan was last refreshed to, so one save refreshes once. */
  lastRevision: Record<string, string>;
  watch: PlanWatchState;
  /** The changes of the watched session, measured for whoever holds the plan. */
  watchImpact: WatchImpactState;
  session: PlanEditSessionState;
}

export const initialPlanEditState: PlanEditState = {
  holders: {},
  lastRevision: {},
  watch: initialPlanWatchState,
  watchImpact: initialWatchImpactState,
  session: initialPlanEditSessionState
};
